import { useCallback, useState, type ReactNode } from 'react';
import { Drawer, type DrawerProps } from './drawer';

export interface MobileMenuControls {
  isOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
}

export type MobileMenuProps = Omit<
  DrawerProps,
  'open' | 'onOpenChange' | 'children'
> & {
  renderTrigger: (controls: MobileMenuControls) => ReactNode;
  children: (controls: MobileMenuControls) => ReactNode;
};

/**
 * Stateful composition around Drawer for mobile navigation. Consumers keep
 * ownership of their trigger and menu content while sharing the open/close
 * wiring.
 */
export function MobileMenu({
  renderTrigger,
  children,
  ...drawerProps
}: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const openMenu = useCallback(() => setIsOpen(true), []);
  const closeMenu = useCallback(() => setIsOpen(false), []);
  const controls = { isOpen, openMenu, closeMenu };

  return (
    <>
      {renderTrigger(controls)}
      <Drawer {...drawerProps} open={isOpen} onOpenChange={setIsOpen}>
        {children(controls)}
      </Drawer>
    </>
  );
}
